import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';
import { CloseIcon, FileIcon, SearchIcon } from '~/assets/icons';
import { useTranslation } from 'react-i18next';
import styles from './CreateReportFile.module.scss';
import classNames from 'classnames/bind';
import { handleCreateReportFile, handleGetJobFiles, handleGetNoteList } from '~/thunks/job/jobThunk';
import { IJob, IJobFile, IReportFile } from '~/utils/interface/job';
import { blobToBase64, cast, handleApiError, sortByKey } from '~/utils/helper';
import { psize, addCustomFont, getFile, isExist, drawPageFromPDF } from '~/utils/pdf';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { useParams } from 'react-router-dom';
import { FileTypeEnum } from '~/utils/constants/file';
import { BaseImage, BaseRow } from '~/components/common';
import dayjs from 'dayjs';
import { getIn, useFormik } from 'formik';
import { INote } from '~/utils/interface/note';
import { NumericFormat } from 'react-number-format';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { applyPlugin, CellHookData, UserOptions } from 'jspdf-autotable';
import { FILE_MIME_TYPE_FORMAT } from '~/utils/constants/common';
import { PDFArray, PDFDocument, PDFName, PDFString, rgb } from 'pdf-lib';
import { handleGetAreaScopeList } from '~/thunks/areaScope/areaScopeThunk';
import { IAreaScope } from '~/utils/interface/areaScope';
import {
  MASTER_DATA_DEBRIS_DISPOSAL,
  MASTER_DATA_EQUIPMENTS,
  MASTER_DATA_PPE_USED,
  MASTER_DATA_TESTING,
} from '~/utils/constants/master';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { cloneDeep, startCase } from 'lodash';
import Compressor from 'compressorjs';
import useQueue from '~/hooks/useQueue';
import { adminRouteAbsolute } from '~/utils/constants/route';
import coverPageUrl from '~/assets/report-cover.pdf';
import {
  DEFAULT_MARGIN_BOTTOM,
  DEFAULT_MARGIN_LEFT,
  DEFAULT_MARGIN_RIGHT,
  DEFAULT_MARGIN_TOP,
  PAGE_FORMAT,
  PDF_GENERATE_HTML_OPTIONS,
  PDF_OPTIONS,
} from '~/utils/constants/pdf';
import { IJsPDFWithPlugin } from '~/utils/interface/common';
import { baseURL } from '~/utils/constants/env';
import { encodeURL } from 'js-base64';
import DataGrid from 'react-data-grid';
import 'react-data-grid/lib/styles.css';

applyPlugin(jsPDF);

type SelectReportFileDialogProps = {
  open: boolean;
  data?: IJob | undefined;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

type GenerateInput = {
  pdfDoc: PDFDocument;
  font: { normal: any; bold: any };
  isGeneratePDFTemp: boolean;
};

// eslint-disable-next-line
const cx = classNames.bind(styles);

const initialValues: IReportFile = {
  name: '',
  fileName: '',
  fileIds: [],
  to: [],
  noteIds: [],
  scopeIds: [],
  numberOfRows: 3,
  numberOfColumns: 2,
  moisturePoint: false,
  psychrometicReadings: false,
};

const STEP_SELECT_FILE = 'selectFile';
const STEP_CREATE_REPORT = 'createReport';

const marginTop = DEFAULT_MARGIN_TOP,
  marginLeft = DEFAULT_MARGIN_LEFT,
  marginRight = DEFAULT_MARGIN_RIGHT,
  marginBottom = DEFAULT_MARGIN_BOTTOM;

let autoTableHeaderStyles = { fillColor: '#efefef', cellPadding: { top: 10, right: 5, bottom: 10, left: 5 } };

let autoTableconfig: UserOptions = {
  showHead: 'firstPage',
  margin: { top: marginTop, right: marginRight, bottom: marginBottom, left: marginLeft },
  theme: 'plain',
  styles: { lineWidth: 1, lineColor: '#d6d6d6' },
};

const CreateReportFileDialog = (props: SelectReportFileDialogProps) => {
  //#region Destructuring Props
  //#endregion Destructuring Props
  const { data } = props;
  //#region Declare Hook
  const { t } = useTranslation('admin/job');
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const queue = useQueue();

  const form = useFormik({
    validateOnChange: false, // Prevent form reset API error
    initialValues,
    onSubmit: async (values) => {
      queue.process({
        handler: async () => {
          // Generate temp file to get page of pdf
          await generatePDF();
          // Generate report file and create file
          const file = await createPDFFile();
          // Save file
          if (file) {
            await dispatch(
              handleCreateReportFile({ id: String(id), params: { file, name: values.name, to: values.to } })
            ).unwrap();
          }
        },
        processNotify: t('files.queue.createReportFile.processNotify', { name: values.fileName }),
        processTitle: t('files.queue.createReportFile.processTitle', { name: values.fileName }),
        successTitle: t('files.queue.createReportFile.successTitle', { name: values.fileName }),
        errorTitle: t('files.queue.createReportFile.errorTitle', { name: values.fileName }),
        navigateUrl: adminRouteAbsolute.jobDetail.replace(':id', `${String(id)}#files`),
      });
      handleSetOpen(false, true);
    },
  });
  //#endregion Declare Hook

  //#region Selector
  const { testing, ppeUsed, debrisDisposal } = useAppSelector((state) => state.master);
  //#endregion Selector

  //#region Declare State
  const [searchInput, setSearchInput] = useState<string>('');
  const [jobFiles, setJobFiles] = useState<Array<IJobFile>>([]);
  const [filterJobFiles, setFilterJobFiles] = useState<Array<IJobFile>>([]);
  const [step, setStep] = useState<string>(STEP_SELECT_FILE);
  const [notes, setNotes] = useState<Array<INote>>([]);
  const { profile } = useAppSelector((state) => state.auth);
  const { areaScopes } = useAppSelector((state) => state.areaScope);
  const { equipments } = useAppSelector((state) => state.master);
  const generatePDFTemp = useRef<any>({
    imagePage: {},
    pdfPage: 0,
    notePage: 0,
    scopePage: 0,
    moisturePointPage: 0,
    psychrometicReadingsPage: 0,
    pdfPageListNumber: [],
  });
  const [isGroupPhotoByTag, setIsGroupPhotoByTag] = useState<boolean>(false);
  const [isIncludeTimestamp, setIsIncludeTimestamp] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_EQUIPMENTS, MASTER_DATA_TESTING, MASTER_DATA_PPE_USED, MASTER_DATA_DEBRIS_DISPOSAL],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (props.open && id) {
      (async () => {
        try {
          const jobFiles = await dispatch(
            handleGetJobFiles({ id: id, data: { limit: 0, getAllInformation: true } })
          ).unwrap();
          const isImageFile = (file: IJobFile) => {
            return file?.mimetype && file.mimetype.indexOf(FILE_MIME_TYPE_FORMAT.IMAGE.INDEX) > -1;
          };
          let imageFiles: Array<IJobFile> = [];
          let otherFiles: Array<IJobFile> = [];
          for (let i = 0; i < jobFiles.responses.length; i++) {
            if (isImageFile(jobFiles.responses[i])) {
              imageFiles.push(jobFiles.responses[i]);
            } else {
              otherFiles.push(jobFiles.responses[i]);
            }
          }
          setJobFiles(
            imageFiles.concat(otherFiles).map((item) => ({
              ...item,
              checked: false,
            }))
          );

          const noteResponse = await dispatch(handleGetNoteList({ jobId: id }))
            .unwrap()
            .catch((e) => {
              handleApiError(e);
            });
          if (noteResponse?.responses) {
            setNotes(noteResponse.responses);
          }
        } catch (e) {
          handleApiError(e);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, id]);

  useEffect(() => {
    if (id) {
      dispatch(handleGetAreaScopeList({ jobId: id }))
        .unwrap()
        .catch(console.error);
    }
  }, [dispatch, id]);

  useEffect(() => {
    setFilterJobFiles([...jobFiles]);
  }, [jobFiles]);

  useEffect(() => {
    form.setFieldValue('name', 'Project Report');
    form.setFieldValue('fileName', props.data?.name);
    form.setFieldValue('to', [profile.email]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchInput(e.target.value);
    const lowerCaseValue = (e.target.value ?? '').toLowerCase();
    setFilterJobFiles([
      ...jobFiles.filter((jobFile: IJobFile) => {
        return (
          jobFile.name.toLowerCase().indexOf(lowerCaseValue) > -1 ||
          (jobFile?.folder?.name ?? '').toLowerCase().indexOf(lowerCaseValue) > -1
        );
      }),
    ]);
  };
  //#endregion Handle Function

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (!open) {
      form.resetForm();
      setStep(STEP_SELECT_FILE);
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const onClickFile = (file: IJobFile) => {
    if (!file.fileId) return;

    if (form.values.fileIds.indexOf(file.fileId) > -1) {
      form.setFieldValue(
        'fileIds',
        form.values.fileIds.filter((fileId: string) => file.fileId !== fileId)
      );
      return;
    }

    form.setFieldValue('fileIds', [...form.values.fileIds, file.fileId]);
  };

  const selectAllFiles = () => {
    form.setFieldValue(
      'fileIds',
      filterJobFiles.map((file: IJobFile) => String(file.fileId))
    );
  };

  const getSelectedFileIndex = (file: IJobFile) => {
    return form.values.fileIds.findIndex((fileId: string) => fileId === file.fileId);
  };

  const renderOrderOfFile = (file: IJobFile) => {
    const index = getSelectedFileIndex(file);
    if (index === -1) return null;

    return (
      <Box className='absolute right-[10px] bottom-[10px] rounded-full bg-[#007afc] w-[25px] h-[25px] flex justify-center items-center text-[#FFFFFF] font-bold'>
        {index + 1}
      </Box>
    );
  };

  const handleCheckboxNote = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    if (checked) {
      return form.setFieldValue('noteIds', [...(form.values.noteIds ?? []), value]);
    }

    form.setFieldValue(
      'noteIds',
      (form.values.noteIds ?? []).filter((id) => id !== value)
    );
  };

  const handleCheckboxScope = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    if (checked) {
      return form.setFieldValue('scopeIds', [...(form.values.scopeIds ?? []), value]);
    }

    form.setFieldValue(
      'scopeIds',
      (form.values.scopeIds ?? []).filter((id) => id !== value)
    );
  };

  const generatePDF = async (isGeneratePDFTemp: boolean = true) => {
    if (isGeneratePDFTemp) {
      generatePDFTemp.current = {
        imagePage: {},
        pdfPage: 0,
        notePage: 0,
        scopePage: 0,
        moisturePointPage: 0,
        psychrometicReadingsPage: 0,
        pdfPageListNumber: [],
      };
    }

    const pdfDoc = await PDFDocument.create();
    const font = await addCustomFont(pdfDoc);
    const generateInput: GenerateInput = {
      isGeneratePDFTemp,
      pdfDoc,
      font,
    };

    if (!isGeneratePDFTemp) {
      await renderCoverPage(generateInput);
    }

    if (!isGeneratePDFTemp) {
      await renderScope(generateInput);
    }

    if (!isGeneratePDFTemp) {
      await renderPsynchrometric(generateInput);
    }

    if (!isGeneratePDFTemp) {
      await renderMoisture(generateInput);
    }

    await renderNote(generateInput);

    if (!isGeneratePDFTemp) {
      await renderFile(generateInput);
    }

    return pdfDoc;
  };

  const createPDFFile = async () => {
    const pdfDoc = await generatePDF(false);
    const pages = pdfDoc.getPages();
    const font = await addCustomFont(pdfDoc);
    const config = {
      x: marginLeft,
      y: 20,
      size: 12,
      font: font.normal,
      color: rgb(0, 0, 0),
    };
    // Add footer
    for (let i = 0; i < pages.length; i++) {
      // Ignore Cover page
      if (i === 0) continue;

      // Ignore embed pdf page number
      if (generatePDFTemp.current.pdfPageListNumber.indexOf(i + 1) > -1) {
        continue;
      }

      const page = pages[i];
      const { width } = page.getSize();

      // Left
      page.drawText(dayjs().format('MMMM D, YYYY'), {
        ...config,
        x: marginLeft,
      });

      // Center
      const centerText = props.data?.name ?? '';
      const centerTextWidth = font.normal.widthOfTextAtSize(centerText, config.size);
      const centerTextX = (width - centerTextWidth) / 2;

      page.drawText(centerText, {
        ...config,
        x: centerTextX,
      });

      const rightText = t('files.footer.page', {
        page: i,
      });
      const rightTextWidth = font.normal.widthOfTextAtSize(rightText, config.size);
      const rightTextX = width - rightTextWidth - marginRight;

      page.drawText(rightText, {
        ...config,
        x: rightTextX,
      });
    }

    const pdfDataUri = (await pdfDoc.save()) as any;

    const blob = new Blob([pdfDataUri], {
      type: FILE_MIME_TYPE_FORMAT.DOCUMENT.PDF,
    });
    const file = new File([blob], `${form.values.fileName || 'report'}.pdf`, {
      type: FILE_MIME_TYPE_FORMAT.DOCUMENT.PDF,
    });
    return file;
  };

  const renderCoverPage = async (input: GenerateInput) => {
    const { pdfDoc, font } = input;

    const responseData = await getFile(coverPageUrl);
    const sourcePdfDoc = await PDFDocument.load(responseData);
    const embeddedPages = await pdfDoc.embedPages(sourcePdfDoc.getPages());

    embeddedPages.forEach(async (embeddedPage) => {
      const page = pdfDoc.addPage(PAGE_FORMAT);
      const width = page.getWidth();
      const height = page.getHeight();
      page.drawPage(embeddedPage, {
        x: 0,
        y: 0,
        width: width,
        height: height,
      });
      page.drawText(`${dayjs().format('MMMM D, YYYY')}`, {
        x: 480,
        y: height - 50,
        size: 14,
        font: font.normal,
      });
      page.drawText(`${data?.name}`, {
        x: width / 2 - font.bold.widthOfTextAtSize(data?.name ?? '', 20) / 2,
        y: 320,
        size: 20,
        color: rgb(247 / 255, 160 / 255, 32 / 255),
        font: font.bold,
      });
      page.drawText(`${data?.customer?.name}`, {
        x: width / 2 - font.normal.widthOfTextAtSize(data?.customer?.name ?? '', 14) / 2,
        y: 270,
        size: 14,
        font: font.normal,
      });
      page.drawText(`${data?.customer?.fullAddress}`, {
        x: width / 2 - font.normal.widthOfTextAtSize(data?.customer?.fullAddress ?? '', 14) / 2,
        y: 250,
        size: 14,
        font: font.normal,
      });
      page.drawText(`Prepared by: ${profile?.name || 'Admin'}`, {
        x: 30,
        y: 50,
        size: 14,
        font: font.normal,
      });
    });
  };

  const renderScope = async (input: GenerateInput) => {
    if (!form.values?.scopeIds?.length) return;
    const selectedScopes = areaScopes.filter((scope: IAreaScope) => form.values.scopeIds.indexOf(scope.id) > -1);
    if (!selectedScopes) return;

    const doc = new jsPDF(PDF_OPTIONS) as IJsPDFWithPlugin;
    const docWidth = doc.internal.pageSize.getWidth() - marginLeft - marginRight;
    let hasData = false;
    // Render Job
    let jobTableBody: any = [];
    if (props.data?.testing?.length) {
      let testingIndex: any;
      for (testingIndex in props.data.testing) {
        const testingId = props.data.testing[testingIndex];
        if (!testing) continue;
        const matchedTesting = testing.find((testingItem) => testingItem.id === testingId);
        if (!matchedTesting) continue;
        jobTableBody.push(['Testing', matchedTesting.name, '1']);
      }
    }

    if (props.data?.jobPPEItems?.length) {
      let jobPPEItemIndex: any;
      for (jobPPEItemIndex in props.data.jobPPEItems) {
        const jobPPEItem = props.data.jobPPEItems[jobPPEItemIndex];
        if (!ppeUsed) continue;
        const matchedjobPPEItem = ppeUsed.find((ppeUsedItem) => ppeUsedItem.id === jobPPEItem.name);
        if (!matchedjobPPEItem) continue;
        jobTableBody.push(['PPE', matchedjobPPEItem.name, jobPPEItem.quantity]);
      }
    }

    if (props?.data?.debrisDisposal && debrisDisposal) {
      const matchedDebrisDisposal = debrisDisposal.find(
        (debrisDisposalItem) => debrisDisposalItem.id === props?.data?.debrisDisposal
      );
      if (matchedDebrisDisposal) {
        jobTableBody.push(['Debris Disposal', matchedDebrisDisposal.name, props.data?.debrisDisposalAmount]);
      }
    }

    if (jobTableBody.length) {
      hasData = true;
      doc.autoTable({
        ...autoTableconfig,
        styles: { ...autoTableconfig.styles, cellWidth: docWidth / 3 },
        head: [
          [
            {
              content: `Area: Job`,
              styles: autoTableHeaderStyles,
              colSpan: 3,
            },
          ],
          ['Category', 'Item', 'Quantity'],
        ],
        body: jobTableBody,
        tableWidth: docWidth,
        margin: { top: 90, right: marginRight, bottom: marginBottom, left: marginLeft },
      });
    }

    let scopeKey: any;
    for (scopeKey in selectedScopes) {
      const scope = selectedScopes[scopeKey];
      if (!scope?.detail || typeof scope.detail !== 'object') continue;

      let tableBody: any = [];
      // Extraction
      if (Array.isArray(scope?.detail?.extraction)) {
        let extractionIndex: any;
        for (extractionIndex in scope?.detail?.extraction) {
          const extraction = scope?.detail?.extraction[extractionIndex];
          if (isExist(extraction?.hardSurface))
            tableBody.push(['Extraction', 'Hard Surface', `${extraction.hardSurface} SF`]);
          if (isExist(extraction?.carpet)) tableBody.push(['Extraction', 'Carpet', `${extraction.carpet} SF`]);
          if (isExist(extraction?.bulk)) tableBody.push(['Extraction', 'Bulk', `${extraction.bulk} SF`]);
          if (isExist(extraction?.heavy)) tableBody.push(['Extraction', 'Heavy', `${extraction.heavy} SF`]);
          if (isExist(extraction?.weight)) tableBody.push(['Extraction', 'Weight', `${extraction.weight} SF`]);
          tableBody = tableBody.concat(handleScopeCustomFields('Extraction', extraction?.customFields));
        }
      }

      // Contents Moved
      if (isExist(scope?.detail?.contentsMoved)) {
        tableBody.push(['Contents Moved', 'Contents Moved', `${scope.detail.contentsMoved} hours`]);
      }
      tableBody = tableBody.concat(handleScopeCustomFields('Contents Moved', scope?.detail?.contentsMovedCustomFields));

      // Cover/Protect
      if (isExist(scope?.detail?.containment))
        tableBody.push(['Cover/Protect', 'Containment', `${scope.detail.containment} SF`]);
      if (isExist(scope?.detail?.zippers)) tableBody.push(['Cover/Protect', 'Zippers', `${scope.detail.zippers} EA`]);
      if (isExist(scope?.detail?.poles)) tableBody.push(['Cover/Protect', 'Poles', `${scope.detail.poles}`]);
      if (isExist(scope?.detail?.layFlat)) tableBody.push(['Cover/Protect', 'Lay Flat', `${scope.detail.layFlat} LF`]);
      if (isExist(scope?.detail?.floorProtection))
        tableBody.push(['Cover/Protect', 'Floor Protection', `${scope.detail.floorProtection} SF`]);

      if (isExist(scope?.detail?.mask)) tableBody.push(['Cover/Protect', 'Mask', `${scope.detail.mask} SF`]);
      if (isExist(scope?.detail?.foamBlocks))
        tableBody.push(['Cover/Protect', 'Foam Blocks', `${scope.detail.foamBlocks}`]);
      if (isExist(scope?.detail?.boxBubble))
        tableBody.push(['Cover/Protect', 'Box / Bubble', `${scope.detail.boxBubble}`]);
      tableBody = tableBody.concat(handleScopeCustomFields('Cover/Protect', scope?.detail?.coverCustomFields));

      // Trim / Finish (lf)
      if (Array.isArray(scope?.detail?.trim)) {
        let trimIndex: any;
        for (trimIndex in scope?.detail?.trim) {
          const trim = scope?.detail?.trim[trimIndex];
          if (isExist(trim?.base)) tableBody.push(['Trim / Finish (lf)', 'Base', `${trim.base} LF`]);
          if (isExist(trim?.cwn)) tableBody.push(['Trim / Finish (lf)', 'CWN', `${trim.cwn} LF`]);
          if (isExist(trim?.tack)) tableBody.push(['Trim / Finish (lf)', 'Tack', `${trim.tack} LF`]);
          if (isExist(trim?.casings)) tableBody.push(['Trim / Finish (lf)', 'Casings', `${trim.casings} LF`]);
          if (isExist(trim?.shoe)) tableBody.push(['Trim / Finish (lf)', 'Shoe', `${trim.shoe} LF`]);
          tableBody = tableBody.concat(handleScopeCustomFields('Trim / Finish (lf)', trim?.customFields));
        }
      }

      // Drywall
      if (isExist(scope?.detail?.drywall?.drywall))
        tableBody.push(['Drywall', 'Drywall', `${scope.detail.drywall.drywall} SF`]);
      if (isExist(scope?.detail?.drywall?.holes))
        tableBody.push(['Drywall', '# Holes', `${scope.detail.drywall.holes} SF`]);

      if (isExist(scope?.detail?.floodCuts?.dispose))
        tableBody.push([
          'Drywall',
          `${scope.detail.floodCuts.dispose} Flood Cut`,
          `${scope?.detail?.drywall?.total || 0} LF`,
        ]);
      if (isExist(scope?.detail?.drywall?.otherWallType))
        tableBody.push(['Other Wall Type', scope?.detail?.drywall?.otherWallType, `${scope?.detail?.drywall?.sf} SF`]);
      tableBody = tableBody.concat(handleScopeCustomFields('Drywall', scope?.detail?.trimCustomFields));

      // Insulation
      if (Array.isArray(scope?.detail?.insulation)) {
        let insulationIndex: any;
        for (insulationIndex in scope?.detail?.insulation) {
          const insulation = scope?.detail?.insulation[insulationIndex];
          if (isExist(insulation?.types))
            tableBody.push(['Insulation', insulation?.types?.join(', '), `${insulation.value} SF`]);
          tableBody = tableBody.concat(handleScopeCustomFields('Insulation', insulation?.customFields));
        }
      }

      // Floor Demo
      if (Array.isArray(scope?.detail?.floorDemo)) {
        let floorDemoIndex: any;
        for (floorDemoIndex in scope?.detail?.floorDemo) {
          const floorDemo = scope?.detail?.floorDemo[floorDemoIndex];
          if (isExist(floorDemo?.carpet)) tableBody.push(['Floor Demo', 'Carpet', `${floorDemo.carpet} SF`]);
          if (isExist(floorDemo?.float)) tableBody.push(['Floor Demo', 'Floating', `${floorDemo.float} SF`]);
          if (isExist(floorDemo?.vinyl)) tableBody.push(['Floor Demo', 'Pad', `${floorDemo.vinyl} SF`]);
          if (isExist(floorDemo?.ul)) tableBody.push(['Floor Demo', 'Engineered', `${floorDemo.ul} SF`]);
          if (isExist(floorDemo?.pad)) tableBody.push(['Floor Demo', 'Tile', `${floorDemo.pad} SF`]);
          if (isExist(floorDemo?.hardwood)) tableBody.push(['Floor Demo', 'Hardwood', `${floorDemo.hardwood} SF`]);
          if (isExist(floorDemo?.lam)) tableBody.push(['Floor Demo', 'Sheet Flooring', `${floorDemo.lam} SF`]);
          if (isExist(floorDemo?.gd)) tableBody.push(['Floor Demo', 'Glued Down', `${floorDemo.gd} SF`]);
          if (isExist(floorDemo?.tile)) tableBody.push(['Floor Demo', 'Underlay', `${floorDemo.tile}`]);
          tableBody = tableBody.concat(handleScopeCustomFields('Floor Demo', floorDemo?.customFields));
        }
      }

      // Countertops & Cabinetry
      if (Array.isArray(scope?.detail?.cabinets)) {
        let cabinetsIndex: any;
        for (cabinetsIndex in scope?.detail?.cabinets) {
          const cabinets = scope?.detail?.cabinets[cabinetsIndex];
          if (isExist(cabinets?.type))
            tableBody.push(['Countertops & Cabinetry', cabinets.type, `${cabinets.counters || 0} LF`]);
          if (isExist(cabinets?.upper)) tableBody.push(['Countertops & Cabinetry', 'Uppers', `${cabinets.upper} LF`]);
          if (isExist(cabinets?.lower)) tableBody.push(['Countertops & Cabinetry', 'Lowers', `${cabinets.lower} LF`]);
          if (isExist(cabinets?.fullHeight))
            tableBody.push(['Countertops & Cabinetry', 'Full Height', `${cabinets.fullHeight} LF`]);
          if (isExist(cabinets?.backsplash))
            tableBody.push(['Countertops & Cabinetry', 'Backsplash', `${cabinets.backsplash} LF`]);
          if (isExist(cabinets?.holes)) tableBody.push(['Countertops & Cabinetry', '# Holes', `${cabinets.holes}`]);
          tableBody = tableBody.concat(handleScopeCustomFields('Countertops & Cabinetry', cabinets?.customFields));
        }
      }

      // Doors Detached
      if (isExist(scope?.detail?.doorsDetached?.slabs))
        tableBody.push(['Doors Detached', 'Dispose', scope.detail.doorsDetached.slabs]);
      tableBody = tableBody.concat(handleScopeCustomFields('Doors Detached', scope?.detail?.doorsDetachedCustomFields));

      // Appliances/Fixtures
      if (Array.isArray(scope?.detail?.appliances)) {
        let appliancesIndex: any;
        for (appliancesIndex in scope?.detail?.appliances) {
          const appliances = scope?.detail?.appliances[appliancesIndex];
          if (isExist(appliances?.dw)) tableBody.push(['Appliances/Fixtures', 'Dishwasher', appliances?.dw]);
          if (isExist(appliances?.rf)) tableBody.push(['Appliances/Fixtures', 'Refrigerator', appliances?.rf]);
          if (isExist(appliances?.rge)) tableBody.push(['Appliances/Fixtures', 'Dryer', appliances.rge]);
          if (isExist(appliances?.rgg)) tableBody.push(['Appliances/Fixtures', 'Range - Gas', appliances.rgg]);
          if (isExist(appliances?.mw)) tableBody.push(['Appliances/Fixtures', 'Microwave', appliances.mw]);
          if (isExist(appliances?.wm)) tableBody.push(['Appliances/Fixtures', 'Washer', appliances.wm]);
          if (isExist(appliances?.dry)) tableBody.push(['Appliances/Fixtures', 'Range - Electric', appliances.dry]);
          if (isExist(appliances?.tlt)) tableBody.push(['Appliances/Fixtures', 'Toilet', appliances.tlt]);
          if (isExist(appliances?.snk)) tableBody.push(['Appliances/Fixtures', 'Sink', appliances.snk]);
          if (isExist(appliances?.psnk)) tableBody.push(['Appliances/Fixtures', 'Pedestal Sink', appliances.psnk]);
          if (isExist(appliances?.caps)) tableBody.push(['Appliances/Fixtures', 'Caps', appliances.caps]);
          tableBody = tableBody.concat(handleScopeCustomFields('Appliances/Fixtures', appliances?.customFields));
        }
      }

      // Clean
      const cleanCategory = 'Clean' + (scope?.detail?.clean ? ` - ${startCase(scope.detail.clean)}` : '');
      if (isExist(scope.detail?.antiMicrobial))
        tableBody.push([cleanCategory, 'Anti Microbial/Biocide', `${scope.detail.antiMicrobial} SF`]);
      if (isExist(scope.detail?.wetWipe)) tableBody.push([cleanCategory, 'Wet Wipe', `${scope.detail.wetWipe} SF`]);
      if (isExist(scope.detail?.hepaVac)) tableBody.push([cleanCategory, 'HEPA Vac', `${scope.detail.hepaVac} SF`]);
      if (isExist(scope.detail?.stainReducer))
        tableBody.push([cleanCategory, 'Stain Reducer', `${scope.detail.stainReducer} SF`]);
      if (isExist(scope.detail?.scrub)) tableBody.push([cleanCategory, 'Scrub/Sand', `${scope.detail.scrub} SF`]);
      if (isExist(scope.detail?.sealant)) tableBody.push([cleanCategory, 'Sealant', `${scope.detail.sealant} SF`]);
      tableBody = tableBody.concat(handleScopeCustomFields(cleanCategory, scope?.detail?.cleanCustomFields));

      const tableHead: any = [
        [
          {
            content: `Area: ${scope.name}`,
            styles: {
              fillColor: '#efefef',
              cellPadding: { top: 10, right: 5, bottom: 10, left: 5 },
            },
          },
        ],
        [
          {
            content: 'Category',
          },
          {
            content: 'Item',
          },
          {
            content: 'Quantity',
          },
        ],
      ];

      const lwh: any = [];
      let colSpan = 1;
      if (scope.detail['length']) lwh.push(`${scope.detail['length']}'`);
      if (scope.detail['width']) lwh.push(`${scope.detail['width']}'`);
      if (scope.detail['height']) lwh.push(`${scope.detail['height']}'`);
      if (!lwh.length) {
        colSpan += 1;
      } else {
        tableHead[0].push({
          content: lwh.join(' x '),
          styles: autoTableHeaderStyles,
        });
      }
      let sfsf = '';
      if (scope.detail['multiplication']) sfsf += `${scope.detail['multiplication']} SF `;
      if (scope.detail['total']) sfsf += `${scope.detail['total']} CF`;

      if (!sfsf) {
        colSpan += 1;
      } else {
        tableHead[0].push({
          content: sfsf,
          styles: autoTableHeaderStyles,
        });
      }

      if (colSpan) {
        tableHead[0][0].colSpan = colSpan;
      }

      if (tableBody.length) {
        hasData = true;
        doc.autoTable({
          ...autoTableconfig,
          styles: { ...autoTableconfig.styles, cellWidth: docWidth / 3 },
          head: tableHead,
          body: tableBody,
          tableWidth: docWidth,
          margin: { top: 90, right: marginRight, bottom: marginBottom, left: marginLeft },
        });
      }

      // Equipment
      let equipmentTableBody: any = [];
      if (Array.isArray(scope?.detail?.heatSource)) {
        let heatSourceIndex: any;
        for (heatSourceIndex in scope?.detail?.heatSource) {
          const heatSource = scope?.detail?.heatSource[heatSourceIndex];
          let days = 0;
          let totalDays = 0;
          if (heatSource?.setDate && heatSource?.pickupDate) {
            days = Math.abs(dayjs(heatSource.setDate).diff(heatSource?.pickupDate, 'days'));
            totalDays = (heatSource?.quantity || 0) * days;
          }
          equipmentTableBody.push([
            heatSource?.type ? equipments?.find((item) => item.id === heatSource.type)?.name || heatSource.type : '',
            heatSource?.quantity ?? '',
            heatSource?.setDate ?? '',
            heatSource?.pickupDate ?? '',
            days || '',
            totalDays || '',
          ]);
        }
      }

      if (equipmentTableBody.length) {
        hasData = true;
        doc.autoTable({
          ...autoTableconfig,
          styles: { ...autoTableconfig.styles, cellWidth: docWidth / 6 },
          head: [
            [
              {
                content: `Equipment: ${scope.name}`,
                styles: autoTableHeaderStyles,
                colSpan: 6,
              },
            ],
            ['Type', 'Quantity', 'Set Date', 'Pickup Date', 'Days', 'Total Days'],
          ],
          body: equipmentTableBody,
          tableWidth: docWidth,
          margin: { top: 90, right: marginRight, bottom: marginBottom, left: marginLeft },
        });
      }
    }

    if (hasData) {
      await drawPageFromPDF({
        ...input,
        doc,
        headerTitle: 'Scope',
      });
    }
  };

  const handleScopeCustomFields = (category: string, customFields: any) => {
    if (!Array.isArray(customFields)) return [];
    let tableBody = [];
    let customFieldIndex;
    for (customFieldIndex in customFields) {
      if (isExist(customFields[customFieldIndex]?.label) || isExist(customFields[customFieldIndex]?.value)) {
        tableBody.push([
          category,
          customFields[customFieldIndex]?.label ?? '',
          customFields[customFieldIndex]?.value ?? '',
        ]);
      }
    }

    return tableBody;
  };

  const renderPsynchrometric = async (input: GenerateInput) => {
    const psychrometicReadings = data?.psychrometicReadings;
    if (!form.values.psychrometicReadings || !psychrometicReadings) return;

    if (
      psychrometicReadings?.outside?.length ||
      psychrometicReadings?.unaffected?.length ||
      psychrometicReadings?.hvac?.length
    ) {
      const doc = new jsPDF(PDF_OPTIONS) as IJsPDFWithPlugin;
      handlePsynchrometricTable({
        title: 'Outside',
        items: psychrometicReadings?.outside,
        doc,
      });

      handlePsynchrometricTable({
        title: 'Unaffected',
        items: psychrometicReadings?.unaffected,
        doc,
      });

      handlePsynchrometricTable({
        title: 'HVAC',
        items: psychrometicReadings?.hvac,
        doc,
      });

      await drawPageFromPDF({
        ...input,
        doc,
        headerTitle: 'Psychrometric Readings',
      });
    }

    let rooms: any = [];
    if (psychrometicReadings?.affected?.length) {
      rooms = psychrometicReadings?.affected.map((affected: any) => {
        return {
          ...affected,
          roomName: affected.roomName || 'Unknown',
          type: 'affected',
        };
      });
    }

    if (psychrometicReadings?.dh?.length) {
      let dhIndex: any;
      for (dhIndex in psychrometicReadings?.dh) {
        const dh = psychrometicReadings?.dh[dhIndex];
        let readingIndex: any = null;
        for (readingIndex in dh?.readings) {
          let reading = dh?.readings[readingIndex];
          reading.dehuName = equipments?.find((item) => item.id === reading.dehu)?.name ?? reading.dehu ?? '';
          rooms.push({
            ...dh,
            readings: [reading],
            roomName: dh.roomName || 'Unknown',
            type: 'dh',
          });
        }
      }
      rooms = rooms.concat();
    }

    if (rooms?.length) {
      let roomKey: any;
      let doc;
      rooms = sortByKey(rooms, 'roomName');

      for (roomKey in rooms) {
        const room = rooms[roomKey];
        if (!room) continue;

        // Create new doc if current room is not the same with previous room
        if (!doc || rooms?.[+roomKey - 1]?.roomName !== room.roomName) {
          doc = new jsPDF(PDF_OPTIONS) as IJsPDFWithPlugin;
        }

        handlePsynchrometricTable({
          title: room.roomName,
          items: room?.readings,
          doc,
          type: room.type,
          dhName: room.dhName,
        });

        // Draw page if current room is not the same with previos room
        if (rooms?.[+roomKey + 1]?.roomName !== room.roomName) {
          await drawPageFromPDF({
            ...input,
            doc,
            headerTitle: `Psychrometric Readings`,
            headerTitle2: room.roomName,
          });
        }
      }
    }
  };

  const handlePsynchrometricTable = (input: any) => {
    const { title, items, doc } = input;
    if (!Array.isArray(items) || !items?.length) return;

    const docWidth = doc.internal.pageSize.getWidth() - marginLeft - marginRight;
    let tableBody: any = [];
    let dehuName: string = '';
    let itemIndex: any;
    for (itemIndex in items) {
      const item = items[itemIndex];
      if (item.dehuName) dehuName = item.dehuName;

      tableBody.push([
        item?.date ? dayjs(item.date).format('MM/DD/YY') : '',
        item?.time ? dayjs(item.time, 'HH:mm').format('hh:mm a') : '',
        item?.rh ?? '',
        item?.temp ?? '',
        item?.gpp ?? '',
      ]);
    }

    if (tableBody.length) {
      doc.autoTable({
        ...autoTableconfig,
        styles: { ...autoTableconfig.styles, cellWidth: docWidth / 5 },
        head: [
          [
            {
              content: `Area: ${title}` + (dehuName ? `     ${dehuName}` : ''),
              colSpan: 5,
              styles: autoTableHeaderStyles,
            },
          ],
          ['Date', 'Time', 'Relative Humidity', 'Temperature', 'Grains Per Pound'],
        ],
        body: tableBody,
        tableWidth: docWidth,
        margin: { top: 90, right: marginRight, bottom: marginBottom, left: marginLeft },
      });
    }
  };

  const renderMoisture = async (input: GenerateInput) => {
    const moisturePoints = data?.moisturePoint?.moisturePoints;
    if (!form.values.moisturePoint || !moisturePoints) return;

    if (moisturePoints?.length) {
      let roomKey: any;
      let doc;
      const rooms = sortByKey(moisturePoints, 'area');

      for (roomKey in rooms) {
        const room = rooms[roomKey];
        if (!room) continue;

        // Create new doc if current room is not the same with previous room
        if (!doc || rooms?.[+roomKey - 1]?.area !== room.area) {
          doc = new jsPDF(PDF_OPTIONS) as IJsPDFWithPlugin;
        }

        handleMoistureTable({
          title: room.area,
          items: room?.readings,
          doc,
          index: roomKey,
        });

        if (rooms?.[+roomKey + 1]?.area !== room.area) {
          await drawPageFromPDF({
            ...input,
            doc,
            headerTitle: `Moisture Readings`,
            headerTitle2: `${room.area ?? 'Unknown'}`,
          });
        }
      }
    }
  };

  const handleMoistureTable = (input: any) => {
    const { title, items, doc, index } = input;

    if (!Array.isArray(items) || !items?.length) return;

    const docWidth = doc.internal.pageSize.getWidth() - marginLeft - marginRight;

    let tableBody: any = [];
    let itemIndex: any;
    for (itemIndex in items) {
      const item = items[itemIndex];
      tableBody.push([
        item?.date ? dayjs(item.date).format('MM/DD/YY') : '',
        startCase(item?.location ?? ''),
        item?.mt ?? '',
        {
          content: item?.value ?? '',
          styles: {
            fillColor: isExist(item?.value) && Number(item?.value) <= Number(item?.goal) ? '#ccf995' : '#FFFFFF',
          },
        },
        item?.goal ?? '',
      ]);
    }

    if (tableBody.length) {
      doc.autoTable({
        ...autoTableconfig,
        styles: { ...autoTableconfig.styles, cellWidth: docWidth / 5 },
        head: [
          [
            {
              content: `Moisture Point: ${+index + 1}     Area: ${title ?? 'Unknown'}`,
              colSpan: 5,
              styles: autoTableHeaderStyles,
            },
          ],
          ['Date', 'Location', 'Material Type', { content: 'Moisture Reading', styles: { fontStyle: 'bold' } }, 'Goal'],
        ],
        headStyles: { fontStyle: 'normal' },
        columnStyles: {
          '3': { fontStyle: 'bold' },
        },
        body: tableBody,
        tableWidth: docWidth,
        margin: { top: 90, right: marginRight, bottom: marginBottom, left: marginLeft },
      });
    }
  };

  const renderNote = async (input: GenerateInput) => {
    const { isGeneratePDFTemp } = input;

    const selectedNotes = notes.filter((note: INote) => form.values.noteIds.indexOf(note.id) > -1);
    if (!selectedNotes.length) return;

    const doc = new jsPDF(PDF_OPTIONS);
    const docWidth = doc.internal.pageSize.getWidth() - marginLeft - marginRight;

    if (!isGeneratePDFTemp) {
      for (let i = 1; i < generatePDFTemp.current.notePage; i++) {
        doc.addPage(PAGE_FORMAT);
      }
    }

    const html = `
        <div class="bor-pdf" style="width: ${psize(docWidth)};
            display: flex;
            flex-direction: column;
            gap: ${psize(20)}
          ">
          ${(() => {
            return selectedNotes
              .map((note) => {
                return `
              <div  style="
                border: ${psize(1)} solid #d6d6d6;
              ">
                <div style="
                  background: #efefef;
                  display: flex;
                  flex-direction: row;
                  gap: ${psize(5)};
                  align-items: center;
                  padding: ${psize(10)};
                ">
                  <div style="font-weight: bold">${
                    note.createdAt ? dayjs(note.createdAt).format('MM/DD/YY hh:mm a') : ''
                  }</div>
                  <div>${note?.createdByAccount?.companyAccount?.name ?? 'Admin'}</div>
                </div>
                <div style="white-space: wrap; padding: ${psize(10)};" class="html-fixed">
                  ${note.note}
                  </div>
              </div>`;
              })
              .join('');
          })()}
      </div>`;

    await doc.html(
      html,
      cloneDeep({
        ...PDF_GENERATE_HTML_OPTIONS,
        margin: [90, marginRight, marginBottom, marginLeft],
      })
    );

    if (isGeneratePDFTemp) {
      const sourcePdfDoc = await PDFDocument.load(doc.output('arraybuffer'));
      generatePDFTemp.current.notePage = sourcePdfDoc.getPages().length;
    } else {
      await drawPageFromPDF({
        ...input,
        doc,
        headerTitle: 'Notes',
      });
    }
  };

  const renderFile = async (input: GenerateInput) => {
    const { pdfDoc } = input;
    const pdfFiles: any = [];
    const imageFiles: any = [];

    let imageGroups: any = [
      {
        id: 0,
        name: 'Uncategorized',
        images: [],
      },
    ];

    const column = form.values.numberOfColumns || 1;
    const row = form.values.numberOfRows || 1;

    form.values.fileIds
      .map((id) => jobFiles.find((file: IJobFile) => file.fileId === id))
      .filter((jobFile): jobFile is IJobFile => Boolean(jobFile?.url && jobFile?.fileId))
      .forEach((jobFile) => {
        if (!Number(jobFile.size)) return;
        if (jobFile?.mimetype && jobFile.mimetype.indexOf(FILE_MIME_TYPE_FORMAT.IMAGE.INDEX) > -1) {
          if (isGroupPhotoByTag) {
            if (jobFile?.tags?.length) {
              let tagId: any;
              for (tagId in jobFile.tags) {
                const tag = jobFile.tags[tagId];
                if (!tag) continue;
                let tagIndex = imageGroups.findIndex((imageGroup: any) => imageGroup.id === tag.id);
                if (tagIndex === -1) {
                  // No tag
                  imageGroups.push({
                    id: tag.id,
                    name: tag.name,
                    images: [],
                  });
                  tagIndex = imageGroups.length - 1;
                }

                // Push image to tags
                imageGroups[tagIndex].images.push(jobFile.fileId);
              }
            } else {
              // Uncategorized image
              let tagIndex = imageGroups.findIndex((imageGroup: any) => imageGroup.id === 0);
              imageGroups[tagIndex].images.push(jobFile.fileId);
            }
          } else {
            // Uncategorized image
            let tagIndex = imageGroups.findIndex((imageGroup: any) => imageGroup.id === 0);
            imageGroups[tagIndex].images.push(jobFile.fileId);
          }

          return imageFiles.push(jobFile);
        }

        if (jobFile?.mimetype === FILE_MIME_TYPE_FORMAT.DOCUMENT.PDF) {
          return pdfFiles.push(jobFile);
        }
      });

    // Render PDF
    const numberOfPage = pdfDoc.getPages().length;
    for (let index in pdfFiles) {
      const renderFile = pdfFiles[index];
      try {
        const response = await axios({
          url: String((renderFile as any)?.url),
          method: 'GET',
          responseType: 'arraybuffer',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        await drawPageFromPDF({
          ...input,
          doc: response.data,
          docType: 'arraybuffer',
          isScale: true,
        });
      } catch (e: any) {
        console.log(e);
      }
    }
    // Calculate for ignore render footer inside pdf file
    let startPdfPage = numberOfPage + 1;
    for (startPdfPage; startPdfPage <= pdfDoc.getPages().length; startPdfPage++) {
      generatePDFTemp.current.pdfPageListNumber.push(startPdfPage);
    }

    // Render Image
    const doc = new jsPDF(PDF_OPTIONS) as IJsPDFWithPlugin;
    const docWidth = doc.internal.pageSize.getWidth() - marginLeft - marginRight;
    const docHeight = doc.internal.pageSize.getHeight() - marginTop - marginBottom;
    let cellWidth = Math.floor(docWidth / column);

    let heightOffset = 0;
    // Increase space to display header title
    if (isGroupPhotoByTag) {
      heightOffset = row * 10;
    }

    let cellHeight = Math.floor(docHeight / row) - heightOffset;

    // Load images
    if (!imageFiles.length) return;
    let jobImageFile: any = {};
    for (let imageIndex = 0; imageIndex < imageFiles.length; imageIndex++) {
      jobImageFile = imageFiles[imageIndex];
      try {
        const response = await axios({
          url: jobImageFile?.url,
          method: 'GET',
          responseType: 'arraybuffer',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        const blob = new Blob([response.data], { type: 'image/jpeg' });

        const compressedImage: Blob | File = await new Promise((resolve, reject) => {
          return new Compressor(blob, {
            quality: 0.4,
            width: cellWidth * 2, // Allow zoom pdf 200%
            height: cellHeight * 2, // Allow zoom pdf 200%
            resize: 'cover',
            retainExif: true,
            mimeType: 'image/jpeg',
            success(result) {
              resolve(result);
            },
            error(err) {
              reject(err);
            },
          });
        });

        jobImageFile.base64 = await blobToBase64(compressedImage);
      } catch (e: any) {
        console.log(e);
      }
    }

    const pageLinks: any = {};
    let imageGroupIndex: any;
    for (imageGroupIndex in imageGroups) {
      const imageGroup = imageGroups[imageGroupIndex];
      const tableBody = [];
      for (let i = 0; i < Math.ceil(imageGroup.images.length / column); i++) {
        const tableRow = [];
        for (let j = 0; j < column; j++) {
          tableRow.push('');
        }
        tableBody.push(tableRow);
      }

      let headOption: any = {
        showHead: 'never',
        head: [[{ content: `Tag: ${imageGroup.name}`, colSpan: column }]],
      };
      if (isGroupPhotoByTag) {
        headOption = {
          showHead: 'firstPage',
          head: [[{ content: `Tag: ${imageGroup.name}`, colSpan: column }]],
        };
      }

      if (!tableBody.length) continue;

      const lineWidth = 10;
      doc.autoTable({
        ...headOption,
        body: tableBody,
        margin: { top: marginTop, right: marginRight, bottom: marginBottom, left: marginLeft },
        theme: 'plain',
        tableWidth: docWidth,
        styles: { cellWidth, minCellHeight: cellHeight, lineWidth, lineColor: 255 },
        headStyles: {
          minCellHeight: 10,
          fontSize: 14,
          fontStyle: 'normal',
        },
        rowPageBreak: 'avoid',
        didDrawCell: (data: CellHookData) => {
          const currentPage = data.doc.internal.getCurrentPageInfo();
          const currentPageNumber: number = currentPage?.pageNumber || 1;
          const cellX = data.cell.x;
          const cellY = data.cell.y;
          const cellWidth = data.cell.width;
          const cellHeight = data.cell.height;
          const cellXInner = cellX + lineWidth / 2; // 1/2 lineWidth
          const cellYInner = cellY + lineWidth / 2; // 1/2 lineWidth
          const cellWidthInner = cellWidth - lineWidth;
          const cellHeightInner = cellHeight - lineWidth;

          if (data.cell.section === 'body') {
            const imageIndex = data.row.index * column + data.column.index;
            const imageId = imageGroup.images[imageIndex];
            const imageData = imageFiles.find((imageFile: any) => imageFile.fileId === imageId);

            if (imageData?.base64) {
              const imageDataX = cellXInner;
              const imageDataY = cellYInner;
              const imageDataWidth = cellWidthInner;
              const imageDataHeight = cellHeightInner;
              const imageDataX2 = imageDataX + imageDataWidth;
              const imageDataY2 = imageDataY + imageDataHeight;

              // Add link list
              if (!pageLinks[currentPageNumber]) {
                pageLinks[currentPageNumber] = [];
              }
              pageLinks[currentPageNumber].push({
                x: imageDataX,
                y: imageDataY,
                width: imageDataWidth,
                height: imageDataHeight,
                previewUrl: `${baseURL}/admin/files/preview/${encodeURL(imageData.url)}`,
                page: currentPage?.pageNumber,
              });

              doc.addImage(imageData.base64, 'JPEG', imageDataX, imageDataY, imageDataWidth, imageDataHeight);
              // Draw image border
              doc.setDrawColor(0, 0, 0);
              doc.setLineWidth(1);
              doc.rect(imageDataX, imageDataY, imageDataWidth, imageDataHeight);

              if (isIncludeTimestamp) {
                doc.setFontSize(12);
                // Draw line 1: Time
                const time = dayjs(imageData.createdAt).format('hh:mm A');
                const timeWidth = doc.getTextWidth(time);
                const timeX = imageDataX2 - timeWidth - 5;
                const timeY = imageDataY2 - 20;
                // Draw text shadow
                doc.setDrawColor(0, 0, 0);

                doc.text(time, timeX, timeY, { renderingMode: 'stroke' });
                doc.setTextColor(255, 255, 255);
                doc.text(time, timeX, timeY);

                // Draw line 2 Date
                const date = dayjs(imageData.createdAt).format('MMM DD, YYYY');
                const dateWidth = doc.getTextWidth(date);
                const dateX = imageDataX2 - dateWidth - 5;
                const dateY = imageDataY2 - 5;
                // Draw text shadow
                doc.setDrawColor(0, 0, 0);
                doc.text(date, dateX, dateY, { renderingMode: 'stroke' });
                doc.setTextColor(255, 255, 255);
                doc.text(date, dateX, dateY);
              }
            }
          }
        },
      });
    }

    const sourcePdfDoc = await PDFDocument.load(doc.output('arraybuffer'));
    const embeddedPages = await pdfDoc.embedPages(sourcePdfDoc.getPages());
    embeddedPages.forEach((embeddedPage: any, index: number) => {
      const page = pdfDoc.addPage(PAGE_FORMAT);
      const { height } = page.getSize();
      page.drawPage(embeddedPage, {
        x: 0,
        y: 0,
      });

      // If page has hyperlink
      const pageNumber = index + 1;
      if (pageLinks[pageNumber]) {
        let linkIndex;
        for (linkIndex in pageLinks[pageNumber]) {
          const link = pageLinks[pageNumber][linkIndex];
          const x1 = link.x;
          const y1 = height - (link.y + link.height);
          const x2 = link.x + link.width;
          const y2 = y1 + link.height;
          const linkAnnotation = pdfDoc.context.obj({
            Type: 'Annot',
            Subtype: 'Link',
            Rect: [x1, y1, x2, y2],
            Border: [0, 0, 0],
            C: [0, 0, 0],
            A: {
              Type: 'Action',
              S: 'URI',
              URI: PDFString.of(link.previewUrl),
            },
          });
          const linkAnnotationRef = pdfDoc.context.register(linkAnnotation);
          const annots = page.node.lookup(PDFName.of('Annots'), PDFArray);
          annots.push(linkAnnotationRef);
        }
      }
    });
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('files.selectFiles')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {step === STEP_SELECT_FILE && (
          <>
            <Box className='flex gap-[20px] mb-[20px]'>
              <TextField
                value={searchInput}
                onChange={(e) => handleSearchChange(e)}
                placeholder={t('files.searchFilePlaceholder')}
                InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
              />
              <Box>
                <Button color='secondary' variant='contained' className='whitespace-nowrap' onClick={selectAllFiles}>
                  {t('files.selectAll')}
                </Button>
              </Box>
            </Box>
            <Box className='w-full h-[calc(100vh-285px)]'>
              <DataGrid
                className='h-[calc(100vh-285px)] overflow-y-scroll'
                headerRowHeight={0}
                rowHeight={120}
                onCellClick={(data: any) => {
                  onClickFile(data.row);
                }}
                columns={[
                  {
                    key: 'id',
                    name: '',
                    cellClass: (row) => {
                      return `cursor-pointer mb-[10px] border rounded-[8px] border-solid border-[#e5eaef] relative w-full text-left ${
                        getSelectedFileIndex(row) > -1 ? 'bg-[#f0f7ff]' : ''
                      }`;
                    },
                    renderCell: (p: { row: any }) => {
                      const file = p.row;
                      return (
                        <Box>
                          {renderOrderOfFile(file)}
                          <Box className='flex justify-flex-start w-full p-3'>
                            <Box onClick={() => window.open(file?.url)} className='w-[80px] h-[80px]'>
                              {file?.type === FileTypeEnum.IMAGE ? (
                                <BaseImage src={file?.url} className='w-full h-full object-cover' />
                              ) : (
                                <FileIcon className='mx-auto my-[15px] w-[70px] h-[70px]' />
                              )}
                            </Box>
                            <Box className='block px-[50px] flex-1'>
                              <Box className='flex mb-1 w-full font-bold'>{file.name}</Box>
                              <Box className='text-[12px]'>
                                {t('files.folder')}: {file.folder?.name}
                              </Box>
                              <Box className='text-[12px]'>
                                {t('files.createdAt')}: {file.createdAt && dayjs(file.createdAt).format('MM/DD/YYYY')}
                              </Box>
                              <Box className='text-[12px]'>
                                {t('files.createdBy')}: {file.createdByAccount}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    },
                  },
                ]}
                rows={filterJobFiles}
              />
            </Box>
          </>
        )}
        {step === STEP_CREATE_REPORT && (
          <>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('files.name')}</InputLabel>
                <TextField
                  name='name'
                  placeholder={t('files.name')}
                  value={cast(form.values?.name)}
                  onChange={form.handleChange}
                  error={Boolean(getIn(form.errors, 'name'))}
                  helperText={getIn(form.errors, 'name')}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('files.fileName')}</InputLabel>
                <TextField
                  name='fileName'
                  placeholder={t('files.fileName')}
                  value={cast(form.values?.fileName)}
                  onChange={form.handleChange}
                  error={Boolean(getIn(form.errors, 'fileName'))}
                  helperText={getIn(form.errors, 'fileName')}
                />
              </Box>
            </BaseRow>
            <Box className='w-full mb-[20px]'>
              <InputLabel>{t('files.to')}</InputLabel>
              <Autocomplete
                multiple
                options={[]}
                value={form.values.to}
                freeSolo
                onChange={(_event, newValue) => {
                  form.setFieldValue('to', newValue);
                }}
                className='multipleline !h-[50px]'
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name='to'
                    error={Boolean(form.errors.to)}
                    helperText={form.errors.to}
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            </Box>
            <Box className='w-full mb-[20px]'>
              <InputLabel>{t('properties.note')}</InputLabel>
              <Box className=' max-h-[300px] overflow-auto'>
                {notes.map((note, index) => {
                  return (
                    <Box
                      className='border rounded-[8px] border-solid border-[#e5eaef] h-[70px] p-[10px] pr-[40px] truncate flex-1 m-1 relative'
                      key={index}
                    >
                      <IconButton className='absolute right-0 top-0'>
                        <Checkbox
                          className='w-[20px] h-[20px]'
                          color='primary'
                          value={note.id}
                          onChange={(event) => handleCheckboxNote(event)}
                          checked={form.values.noteIds.includes(note.id)}
                        />
                      </IconButton>
                      <Box className='line-clamp-3' dangerouslySetInnerHTML={{ __html: note.note }} />
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box className='w-full mb-[20px]'>
              <InputLabel>{t('properties.scope')}</InputLabel>
              <Box className=' max-h-[300px] overflow-auto'>
                {areaScopes.map((scope, index) => {
                  return (
                    <Box
                      className='border rounded-[8px] border-solid border-[#e5eaef] h-[70px] p-[10px] pr-[40px] truncate flex-1 m-1 relative'
                      key={index}
                    >
                      <IconButton className='absolute right-0 top-0'>
                        <Checkbox
                          className='w-[20px] h-[20px]'
                          color='primary'
                          value={scope.id}
                          onChange={(event) => handleCheckboxScope(event)}
                          checked={form.values.scopeIds.includes(scope.id)}
                        />
                      </IconButton>
                      <Box className='line-clamp-3' dangerouslySetInnerHTML={{ __html: scope.name }} />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box className='border rounded-[8px] border-solid border-[#e5eaef] h-[70px] p-[10px] pr-[40px] truncate flex-1 m-1 relative mb-[20px]'>
              <InputLabel>{t('areaMoisturePoints')}</InputLabel>
              <IconButton className='absolute right-0 top-0'>
                <Checkbox
                  className='w-[20px] h-[20px]'
                  color='primary'
                  value={form.values.moisturePoint}
                  onChange={() => {
                    form.setFieldValue('moisturePoint', !form.values.moisturePoint);
                  }}
                  checked={form.values.moisturePoint === true}
                />
              </IconButton>
            </Box>
            <Box className='border rounded-[8px] border-solid border-[#e5eaef] h-[70px] p-[10px] pr-[40px] truncate flex-1 m-1 relative mb-[20px]'>
              <InputLabel>{t('areaPsychrometricReadings')}</InputLabel>
              <IconButton className='absolute right-0 top-0'>
                <Checkbox
                  className='w-[20px] h-[20px]'
                  color='primary'
                  value={form.values.psychrometicReadings}
                  onChange={() => {
                    form.setFieldValue('psychrometicReadings', !form.values.psychrometicReadings);
                  }}
                  checked={form.values.psychrometicReadings === true}
                />
              </IconButton>
            </Box>
            <BaseRow className='flex gap-[10px]'>
              <Box className='w-full'>
                <InputLabel>{t('files.numberOfRows')}</InputLabel>
                <NumericFormat
                  customInput={TextField}
                  value={form.values.numberOfRows}
                  onValueChange={(val) => {
                    form.setFieldValue('numberOfRows', val.floatValue);
                  }}
                  decimalScale={2}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('files.numberOfColumns')}</InputLabel>
                <NumericFormat
                  customInput={TextField}
                  value={form.values.numberOfColumns}
                  onValueChange={(val) => {
                    form.setFieldValue('numberOfColumns', val.floatValue);
                  }}
                  decimalScale={2}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel className='invisible'>Label</InputLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      className='w-[20px] h-[20px]'
                      color='primary'
                      onChange={(event) => setIsGroupPhotoByTag(event.target.checked)}
                      checked={isGroupPhotoByTag}
                    />
                  }
                  label={t('files.isGroupPhotoByTag')}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel className='invisible'>Label</InputLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      className='w-[20px] h-[20px]'
                      color='primary'
                      onChange={(event) => setIsIncludeTimestamp(event.target.checked)}
                      checked={isIncludeTimestamp}
                    />
                  }
                  label={t('files.isIncludeTimestamp')}
                />
              </Box>
            </BaseRow>
          </>
        )}
        {/* Render Note HTML */}
        <Box className='pdf-debug-render'></Box>
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        {step === STEP_SELECT_FILE && (
          <Button color='primary' variant='contained' onClick={() => setStep(STEP_CREATE_REPORT)}>
            {t('files.pdfReport')}
          </Button>
        )}
        {step === STEP_CREATE_REPORT && (
          <Button color='secondary' variant='contained' onClick={() => setStep(STEP_SELECT_FILE)}>
            {t('back', { ns: 'admin/common' })}
          </Button>
        )}
        {step === STEP_CREATE_REPORT && (
          <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
            {t('files.generate')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default memo(CreateReportFileDialog);
